import '../scss/components/_addelectricityreadingmodal.scss';
import React, { useState, useEffect } from 'react';
import { insertElectricityReading, getElectricityByMonthYear, GetPreviousMonthReadingValue, GetAllElectricityReadings } from '../apis/services/pgApi';

// ─── Month Options ───────────────────────────────────────────────
const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// ─── Year Options ────────────────────────────────────────────────
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 2; i++) {
    years.push({ value: i, label: i.toString() });
  }
  return years;
};

// ─── AddElectricityReadingModal Component ────────────────────────
const AddElectricityReadingModal = ({ isOpen, onClose, selectedPG, rooms, onReadingAdded }) => {
  const [form, setForm] = useState({
    roomId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    readingDate: new Date().toISOString().split('T')[0], // Add this
    previousReading: '',
    currentReading: '',
    ratePerUnit: ''
  });

  const [existingReading, setExistingReading] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [units, setUnits] = useState(null);
  const [lastThreeReadings, setLastThreeReadings] = useState([]);
  const [loadingReadings, setLoadingReadings] = useState(false);

  // Fetch existing reading when room, month, or year changes
  useEffect(() => {
    const fetchExistingReading = async () => {
      if (!selectedPG?.PGID || !form.roomId || !form.month || !form.year) {
        setExistingReading(null);
        return;
      }

      setFetching(true);
      try {
        const data = {
            PGID: selectedPG.PGID,
            RoomId: parseInt(form.roomId),
            Month: parseInt(form.month),
            Year: parseInt(form.year)
        };
        const response = await GetPreviousMonthReadingValue(data);
        console.log("response from getElectricityByMonthYear:", response);
        
        if (response && response.PreviousMonthReading) {
          setExistingReading(response);
          setForm(prev => ({
            ...prev,
            previousReading: response.PreviousMonthReading?.toString() || '',
          }));
        } else {
          setExistingReading(null);
          setForm(prev => ({
            ...prev,
            previousReading: '',
          }));
        }
      } catch (error) {
        console.error('Error fetching existing reading:', error);
        setExistingReading(null);
      } finally {
        setFetching(false);
      }
    };

    fetchExistingReading();
  }, [selectedPG?.PGID, form.roomId, form.month, form.year]);

  // Fetch last 3 readings when room is selected
  useEffect(() => {
    const fetchLastThreeReadings = async () => {
      if (!selectedPG?.PGID || !form.roomId) {
        setLastThreeReadings([]);
        return;
      }

      setLoadingReadings(true);
      try {
        const response = await GetAllElectricityReadings(selectedPG.PGID, form.roomId);
        console.log("All electricity readings:", response);
        
        if (response && Array.isArray(response)) {
          // Sort by year and month descending (most recent first)
          const sortedReadings = [...response].sort((a, b) => {
            if (a.Year !== b.Year) return b.Year - a.Year;
            return b.Month - a.Month;
          });
          // Get last 3 readings
          setLastThreeReadings(sortedReadings.slice(0, 3));
        } else {
          setLastThreeReadings([]);
        }
      } catch (error) {
        console.error('Error fetching last three readings:', error);
        setLastThreeReadings([]);
      } finally {
        setLoadingReadings(false);
      }
    };

    fetchLastThreeReadings();
  }, [selectedPG?.PGID, form.roomId]);

  if (!isOpen) return null;

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    
    // Calculate units when both readings are present
    if (field === 'previousReading' || field === 'currentReading') {
      const prev = field === 'previousReading' ? parseFloat(value) : parseFloat(form.previousReading);
      const curr = field === 'currentReading' ? parseFloat(value) : parseFloat(form.currentReading);
      if (!isNaN(prev) && !isNaN(curr) && curr >= prev) {
        setUnits(curr - prev);
      } else {
        setUnits(null);
      }
    }
  };

  const reset = () => {
    setForm({
      roomId: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      readingDate: new Date().toISOString().split('T')[0],
      previousReading: '',
      currentReading: '',
      ratePerUnit: ''
    });
    setExistingReading(null);
    setErrors({});
    setApiError(null);
    setUnits(null);
    setLastThreeReadings([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = () => {
    const e = {};
    if (!form.roomId) e.roomId = 'Please select a room';
    if (!form.month) e.month = 'Please select month';
    if (!form.year) e.year = 'Please select year';
    if (!form.readingDate) e.readingDate = 'Please select reading date';
    if (!form.previousReading) e.previousReading = 'Previous reading is required';
    if (!form.currentReading) e.currentReading = 'Current reading is required';
    if (!form.ratePerUnit) e.ratePerUnit = 'Rate per unit is required';
    
    const prev = parseFloat(form.previousReading);
    const curr = parseFloat(form.currentReading);
    
    if (!isNaN(prev) && !isNaN(curr) && curr < prev) {
      e.currentReading = 'Current reading cannot be less than previous reading';
    }
    
    if (form.ratePerUnit && parseFloat(form.ratePerUnit) <= 0) {
      e.ratePerUnit = 'Rate per unit must be greater than 0';
    }
    
    return e;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    if (!selectedPG || !selectedPG.PGID) {
      setApiError('No PG selected. Please select a PG from the dashboard.');
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      const readingData = {
        PGID: Number(selectedPG.PGID),
        RoomId: Number(form.roomId),
        Month: parseInt(form.month),
        Year: parseInt(form.year),
        PreviousReading: parseFloat(form.previousReading),
        CurrentReading: parseFloat(form.currentReading),
        RatePerUnit: parseFloat(form.ratePerUnit),
        ReadingDate: form.readingDate // Add this
      };

      console.log("Sending reading data:", readingData);

      const response = await insertElectricityReading(readingData);

      if (response && response.Success === 1) {
        if (onReadingAdded) {
          onReadingAdded({
            ...form,
            units: units,
            totalAmount: units * parseFloat(form.ratePerUnit),
            submittedAt: new Date().toISOString(),
            isUpdate: !!existingReading
          });
        }

        alert(existingReading ? 'Electricity reading updated successfully!' : 'Electricity reading added successfully!');
        reset();
        onClose();
      } else {
        throw new Error(response?.Message || 'Failed to add electricity reading');
      }
    } catch (error) {
      console.error('Error adding electricity reading:', error);
      setApiError(error.message || 'Failed to add electricity reading. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const selectedRoom = rooms?.find(r => r.RoomID === parseInt(form.roomId));

  return (
    <div className="aerm-overlay" >
      <div className="aerm-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="aerm-head">
          <div>
            <h2 className="aerm-title">
              {existingReading ? 'Edit Electricity Reading' : 'Add Electricity Reading'}
            </h2>
            {selectedPG && (
              <p className="aerm-sub">
                PG: <strong>{selectedPG.PGName}</strong>
                {selectedPG.PGCode && <span className="aerm-pg-code"> ({selectedPG.PGCode})</span>}
              </p>
            )}
          </div>
          <button className="aerm-close" onClick={handleClose}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* API Error */}
        {apiError && (
          <div className="aerm-error-box">
            <strong>Error:</strong> {apiError}
          </div>
        )}

        {/* Existing Reading Info */}
        {existingReading && (
          <div className="aerm-info-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>
              Existing reading found for {monthOptions.find(m => m.value === form.month)?.label} {form.year}. 
              {/* You can update the values below. */}
            </span>
          </div>
        )}

        {/* Fetching Indicator */}
        {fetching && (
          <div className="aerm-loading-box">
            <div className="aerm-small-spinner"></div>
            <span>Loading existing data...</span>
          </div>
        )}

        {/* Form Body */}
        <div className="aerm-form-body">
          <div className="aerm-section">
            <h4 className="aerm-section-title">Reading Details</h4>
            <div className="aerm-grid">
              
              <div className={`aerm-field ${errors.roomId ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Select Room <span className="aerm-req">*</span></label>
                <select 
                  className="aerm-select" 
                  value={form.roomId} 
                  onChange={e => set('roomId', e.target.value)}
                  disabled={fetching}
                >
                  <option value="">Select a room</option>
                  {rooms && rooms.map(room => (
                    <option key={room.RoomID} value={room.RoomID}>
                      Room {room.Room}
                    </option>
                  ))}
                </select>
                {errors.roomId && <span className="aerm-err-msg">{errors.roomId}</span>}
              </div>
              
              <div className={`aerm-field ${errors.month ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Month <span className="aerm-req">*</span></label>
                <select 
                  className="aerm-select" 
                  value={form.month} 
                  onChange={e => set('month', e.target.value)}
                  disabled={fetching}
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.month && <span className="aerm-err-msg">{errors.month}</span>}
              </div>
              
              <div className={`aerm-field ${errors.year ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Year <span className="aerm-req">*</span></label>
                <select 
                  className="aerm-select" 
                  value={form.year} 
                  onChange={e => set('year', e.target.value)}
                  disabled={fetching}
                >
                  {getYearOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.year && <span className="aerm-err-msg">{errors.year}</span>}
              </div>

              {/* NEW DATE INPUT FIELD */}
              <div className={`aerm-field ${errors.readingDate ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Reading Date <span className="aerm-req">*</span></label>
                <input 
                  className="aerm-input" 
                  type="date" 
                  value={form.readingDate} 
                  onChange={e => set('readingDate', e.target.value)}
                  disabled={fetching}
                />
                {errors.readingDate && <span className="aerm-err-msg">{errors.readingDate}</span>}
              </div>

              <div className={`aerm-field ${errors.previousReading ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Previous Reading (Units) <span className="aerm-req">*</span></label>
                <input 
                  className="aerm-input" 
                  type="number" 
                  step="0.01"
                  placeholder="e.g. 1250.50" 
                  value={form.previousReading} 
                  onChange={e => set('previousReading', e.target.value)} 
                />
                {errors.previousReading && <span className="aerm-err-msg">{errors.previousReading}</span>}
              </div>

              <div className={`aerm-field ${errors.currentReading ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Current Reading (Units) <span className="aerm-req">*</span></label>
                <input 
                  className="aerm-input" 
                  type="number" 
                  step="0.01"
                  placeholder="e.g. 1350.75" 
                  value={form.currentReading} 
                  onChange={e => set('currentReading', e.target.value)} 
                />
                {errors.currentReading && <span className="aerm-err-msg">{errors.currentReading}</span>}
              </div>

              <div className={`aerm-field ${errors.ratePerUnit ? 'aerm-field-err' : ''}`}>
                <label className="aerm-label">Rate per Unit (₹) <span className="aerm-req">*</span></label>
                <input 
                  className="aerm-input" 
                  type="number" 
                  step="0.01"
                  placeholder="e.g. 8.50" 
                  value={form.ratePerUnit} 
                  onChange={e => set('ratePerUnit', e.target.value)} 
                />
                {errors.ratePerUnit && <span className="aerm-err-msg">{errors.ratePerUnit}</span>}
              </div>

              {/* Summary Card */}
              {units !== null && form.ratePerUnit && (
                <div className="aerm-summary">
                  <div className="aerm-summary-item">
                    <span className="aerm-summary-label">Units Consumed:</span>
                    <span className="aerm-summary-value">{units.toFixed(2)} units</span>
                  </div>
                  <div className="aerm-summary-item">
                    <span className="aerm-summary-label">Total Amount:</span>
                    <span className="aerm-summary-value-highlight">₹ {(units * parseFloat(form.ratePerUnit)).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Last 3 Readings Table - shown when room is selected */}
          {form.roomId && (
            <div className="aerm-last-readings-section">
              <h4 className="aerm-section-title">Last 3 Readings</h4>
              {loadingReadings ? (
                <div className="aerm-loading-readings">
                  <div className="aerm-small-spinner"></div>
                  <span>Loading reading history...</span>
                </div>
              ) : lastThreeReadings.length > 0 ? (
                <div className="aerm-readings-table-wrapper">
                  <table className="aerm-readings-table">
                    <thead>
                      <tr>
                        <th>Month/Year</th>
                        <th>Previous Reading</th>
                        <th>Current Reading</th>
                        <th>Units</th>
                        <th>Rate/Unit</th>
                        <th>Total Amount</th>
                        <th>Reading Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastThreeReadings.map((reading, index) => (
                        <tr key={reading.ReadingID || index}>
                          <td>
                            {monthOptions.find(m => m.value === reading.Month)?.label} {reading.Year}
                          </td>
                          <td>{reading.PreviousReading}</td>
                          <td>{reading.CurrentReading}</td>
                          <td>{reading.UnitsConsumed}</td>
                          <td>₹{reading.RatePerUnit}</td>
                          <td className="aerm-amount-cell">₹{reading.TotalAmount}</td>
                          <td>{formatDate(reading.ReadingDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="aerm-no-readings">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                    <path d="M3 3v16a2 2 0 002 2h14a2 2 0 002-2V7l-5-5H5a2 2 0 00-2 2z"/>
                    <path d="M13 3v4h4"/>
                  </svg>
                  <p>No previous readings found for this room.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="aerm-footer">
          <button className="aerm-btn aerm-btn-outline" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className="aerm-btn aerm-btn-primary" onClick={handleSubmit} disabled={loading || fetching}>
            {loading ? (
              <>
                <span className="aerm-spinner"></span>
                {existingReading ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              <span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
                {/* {existingReading ? 'Update Reading' : 'Add Reading'} */}
                 Add Reading
              </span>
            )}
          </button>
        </div>
      </div>

      <style>{`
        .aerm-spinner, .aerm-small-spinner {
          display: inline-block;
          width: 12px;
          height: 12px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: aerm-spin 0.6s linear infinite;
          margin-right: 8px;
        }
        
        .aerm-small-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid #6366F1;
          border-top-color: transparent;
        }
        
        @keyframes aerm-spin {
          to { transform: rotate(360deg); }
        }
        
        .aerm-error-box {
          margin: 16px 24px;
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FEE2E2;
          border-radius: 8px;
          color: #DC2626;
          font-size: 13px;
        }
        
        .aerm-info-box {
          margin: 16px 24px;
          padding: 12px;
          background: #EFF6FF;
          border: 1px solid #BFDBFE;
          border-radius: 8px;
          color: #1D4ED8;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .aerm-loading-box {
          margin: 16px 24px;
          padding: 12px;
          background: #F3F4F6;
          border-radius: 8px;
          color: #6B7280;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .aerm-summary {
          grid-column: span 2;
          background: #F0FDF4;
          border: 1px solid #86EFAC;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .aerm-summary-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .aerm-summary-label {
          font-size: 12px;
          color: #6B7280;
        }
        
        .aerm-summary-value {
          font-size: 18px;
          font-weight: 600;
          color: #166534;
        }
        
        .aerm-summary-value-highlight {
          font-size: 20px;
          font-weight: 700;
          color: #16A34A;
        }
        
        /* Last 3 Readings Table Styles */
        .aerm-last-readings-section {
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid #E5E7EB;
        }
        
        .aerm-loading-readings {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 32px;
          background: #F9FAFB;
          border-radius: 12px;
          color: #6B7280;
        }
        
        .aerm-readings-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
        }
        
        .aerm-readings-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        
        .aerm-readings-table thead tr {
          background: #F3F4F6;
        }
        
        .aerm-readings-table th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #E5E7EB;
        }
        
        .aerm-readings-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #F3F4F6;
          color: #4B5563;
        }
        
        .aerm-readings-table tr:last-child td {
          border-bottom: none;
        }
        
        .aerm-readings-table tr:hover td {
          background: #F9FAFB;
        }
        
        .aerm-amount-cell {
          font-weight: 600;
          color: #059669;
        }
        
        .aerm-no-readings {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px;
          background: #F9FAFB;
          border-radius: 12px;
          border: 1px dashed #D1D5DB;
          color: #6B7280;
        }
        
        .aerm-no-readings p {
          margin: 0;
          font-size: 14px;
        }
        
        .aerm-room-info {
          margin-top: 24px;
          padding: 16px;
          background: #F3F4F6;
          border-radius: 12px;
        }
        
        .aerm-room-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-top: 12px;
        }
        
        .aerm-room-detail {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: white;
          border-radius: 8px;
        }
        
        .aerm-room-label {
          font-size: 12px;
          color: #6B7280;
        }
        
        .aerm-room-value {
          font-size: 14px;
          font-weight: 500;
          color: #1F2937;
        }
      `}</style>
    </div>
  );
};

export default AddElectricityReadingModal;

// import '../scss/components/_addelectricityreadingmodal.scss';
// import React, { useState, useEffect } from 'react';
// import { insertElectricityReading, getElectricityByMonthYear, GetPreviousMonthReadingValue, GetAllElectricityReadings } from '../apis/services/pgApi';

// // ─── Month Options ───────────────────────────────────────────────
// const monthOptions = [
//   { value: 1, label: "January" },
//   { value: 2, label: "February" },
//   { value: 3, label: "March" },
//   { value: 4, label: "April" },
//   { value: 5, label: "May" },
//   { value: 6, label: "June" },
//   { value: 7, label: "July" },
//   { value: 8, label: "August" },
//   { value: 9, label: "September" },
//   { value: 10, label: "October" },
//   { value: 11, label: "November" },
//   { value: 12, label: "December" },
// ];

// // ─── Year Options ────────────────────────────────────────────────
// const getYearOptions = () => {
//   const currentYear = new Date().getFullYear();
//   const years = [];
//   for (let i = currentYear - 2; i <= currentYear + 2; i++) {
//     years.push({ value: i, label: i.toString() });
//   }
//   return years;
// };

// // ─── AddElectricityReadingModal Component ────────────────────────
// const AddElectricityReadingModal = ({ isOpen, onClose, selectedPG, rooms, onReadingAdded }) => {
//   const [form, setForm] = useState({
//     roomId: '',
//     month: new Date().getMonth() + 1,
//     year: new Date().getFullYear(),
//     previousReading: '',
//     currentReading: '',
//     ratePerUnit: ''
//   });

//   const [existingReading, setExistingReading] = useState(null);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [fetching, setFetching] = useState(false);
//   const [apiError, setApiError] = useState(null);
//   const [units, setUnits] = useState(null);
//   const [lastThreeReadings, setLastThreeReadings] = useState([]);
//   const [loadingReadings, setLoadingReadings] = useState(false);

//   // Fetch existing reading when room, month, or year changes
//   useEffect(() => {
//     const fetchExistingReading = async () => {
//       if (!selectedPG?.PGID || !form.roomId || !form.month || !form.year) {
//         setExistingReading(null);
//         return;
//       }

//       setFetching(true);
//       try {
//         const data = {
//             PGID: selectedPG.PGID,
//             RoomId: parseInt(form.roomId),
//             Month: parseInt(form.month),
//             Year: parseInt(form.year)
//         };
//         const response = await GetPreviousMonthReadingValue(data);
//         console.log("response from getElectricityByMonthYear:", response);
        
//         if (response && response.PreviousMonthReading) {
//           setExistingReading(response);
//           setForm(prev => ({
//             ...prev,
//             previousReading: response.PreviousMonthReading?.toString() || '',
//           }));
//         } else {
//           setExistingReading(null);
//         }
//       } catch (error) {
//         console.error('Error fetching existing reading:', error);
//         setExistingReading(null);
//       } finally {
//         setFetching(false);
//       }
//     };

//     fetchExistingReading();
//   }, [selectedPG?.PGID, form.roomId, form.month, form.year]);

//   // Fetch last 3 readings when room is selected
//   useEffect(() => {
//     const fetchLastThreeReadings = async () => {
//       if (!selectedPG?.PGID || !form.roomId) {
//         setLastThreeReadings([]);
//         return;
//       }

//       setLoadingReadings(true);
//       try {
//         const response = await GetAllElectricityReadings(selectedPG.PGID, form.roomId);
//         console.log("All electricity readings:", response);
        
//         if (response && Array.isArray(response)) {
//           // Sort by year and month descending (most recent first)
//           const sortedReadings = [...response].sort((a, b) => {
//             if (a.Year !== b.Year) return b.Year - a.Year;
//             return b.Month - a.Month;
//           });
//           // Get last 3 readings
//           setLastThreeReadings(sortedReadings.slice(0, 3));
//         } else {
//           setLastThreeReadings([]);
//         }
//       } catch (error) {
//         console.error('Error fetching last three readings:', error);
//         setLastThreeReadings([]);
//       } finally {
//         setLoadingReadings(false);
//       }
//     };

//     fetchLastThreeReadings();
//   }, [selectedPG?.PGID, form.roomId]);

//   if (!isOpen) return null;

//   const set = (field, value) => {
//     setForm(prev => ({ ...prev, [field]: value }));
//     setErrors(prev => ({ ...prev, [field]: undefined }));
    
//     // Calculate units when both readings are present
//     if (field === 'previousReading' || field === 'currentReading') {
//       const prev = field === 'previousReading' ? parseFloat(value) : parseFloat(form.previousReading);
//       const curr = field === 'currentReading' ? parseFloat(value) : parseFloat(form.currentReading);
//       if (!isNaN(prev) && !isNaN(curr) && curr >= prev) {
//         setUnits(curr - prev);
//       } else {
//         setUnits(null);
//       }
//     }
//   };

//   const reset = () => {
//     setForm({
//       roomId: '',
//       month: new Date().getMonth() + 1,
//       year: new Date().getFullYear(),
//       previousReading: '',
//       currentReading: '',
//       ratePerUnit: ''
//     });
//     setExistingReading(null);
//     setErrors({});
//     setApiError(null);
//     setUnits(null);
//     setLastThreeReadings([]);
//   };

//   const handleClose = () => {
//     reset();
//     onClose();
//   };

//   const validate = () => {
//     const e = {};
//     if (!form.roomId) e.roomId = 'Please select a room';
//     if (!form.month) e.month = 'Please select month';
//     if (!form.year) e.year = 'Please select year';
//     if (!form.previousReading) e.previousReading = 'Previous reading is required';
//     if (!form.currentReading) e.currentReading = 'Current reading is required';
//     if (!form.ratePerUnit) e.ratePerUnit = 'Rate per unit is required';
    
//     const prev = parseFloat(form.previousReading);
//     const curr = parseFloat(form.currentReading);
    
//     if (!isNaN(prev) && !isNaN(curr) && curr < prev) {
//       e.currentReading = 'Current reading cannot be less than previous reading';
//     }
    
//     if (form.ratePerUnit && parseFloat(form.ratePerUnit) <= 0) {
//       e.ratePerUnit = 'Rate per unit must be greater than 0';
//     }
    
//     return e;
//   };

//   const handleSubmit = async () => {
//     const validationErrors = validate();
//     if (Object.keys(validationErrors).length) {
//       setErrors(validationErrors);
//       return;
//     }

//     if (!selectedPG || !selectedPG.PGID) {
//       setApiError('No PG selected. Please select a PG from the dashboard.');
//       return;
//     }

//     setLoading(true);
//     setApiError(null);

//     try {
//       const readingData = {
//         PGID: Number(selectedPG.PGID),
//         RoomId: Number(form.roomId),
//         Month: parseInt(form.month),
//         Year: parseInt(form.year),
//         PreviousReading: parseFloat(form.previousReading),
//         CurrentReading: parseInt(form.currentReading),
//         RatePerUnit: parseInt(form.ratePerUnit)
//       };

//       const response = await insertElectricityReading(readingData);

//       if (response && response.Success === 1) {
//         if (onReadingAdded) {
//           onReadingAdded({
//             ...form,
//             units: units,
//             totalAmount: units * parseFloat(form.ratePerUnit),
//             submittedAt: new Date().toISOString(),
//             isUpdate: !!existingReading
//           });
//         }

//         alert(existingReading ? 'Electricity reading updated successfully!' : 'Electricity reading added successfully!');
//         reset();
//         onClose();
//       } else {
//         throw new Error(response?.Message || 'Failed to add electricity reading');
//       }
//     } catch (error) {
//       console.error('Error adding electricity reading:', error);
//       setApiError(error.message || 'Failed to add electricity reading. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return '-';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
//   };

//   const selectedRoom = rooms?.find(r => r.RoomID === parseInt(form.roomId));

//   return (
//     <div className="aerm-overlay" >
//       <div className="aerm-modal" onClick={e => e.stopPropagation()}>
        
//         {/* Header */}
//         <div className="aerm-head">
//           <div>
//             <h2 className="aerm-title">
//               {existingReading ? 'Edit Electricity Reading' : 'Add Electricity Reading'}
//             </h2>
//             {selectedPG && (
//               <p className="aerm-sub">
//                 PG: <strong>{selectedPG.PGName}</strong>
//                 {selectedPG.PGCode && <span className="aerm-pg-code"> ({selectedPG.PGCode})</span>}
//               </p>
//             )}
//           </div>
//           <button className="aerm-close" onClick={handleClose}>
//             <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//         </div>

//         {/* API Error */}
//         {apiError && (
//           <div className="aerm-error-box">
//             <strong>Error:</strong> {apiError}
//           </div>
//         )}

//         {/* Existing Reading Info */}
//         {existingReading && (
//           <div className="aerm-info-box">
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
//               <circle cx="12" cy="12" r="10"/>
//               <line x1="12" y1="16" x2="12" y2="12"/>
//               <line x1="12" y1="8" x2="12.01" y2="8"/>
//             </svg>
//             <span>
//               Existing reading found for {monthOptions.find(m => m.value === form.month)?.label} {form.year}. 
//               You can update the values below.
//             </span>
//           </div>
//         )}

//         {/* Fetching Indicator */}
//         {fetching && (
//           <div className="aerm-loading-box">
//             <div className="aerm-small-spinner"></div>
//             <span>Loading existing data...</span>
//           </div>
//         )}

//         {/* Form Body */}
//         <div className="aerm-form-body">
//           <div className="aerm-section">
//             <h4 className="aerm-section-title">Reading Details</h4>
//             <div className="aerm-grid">
              
//               <div className={`aerm-field ${errors.roomId ? 'aerm-field-err' : ''}`}>
//                 <label className="aerm-label">Select Room <span className="aerm-req">*</span></label>
//                 <select 
//                   className="aerm-select" 
//                   value={form.roomId} 
//                   onChange={e => set('roomId', e.target.value)}
//                   disabled={fetching}
//                 >
//                   <option value="">Select a room</option>
//                   {rooms && rooms.map(room => (
//                     <option key={room.RoomID} value={room.RoomID}>
//                       Room {room.Room}
//                     </option>
//                   ))}
//                 </select>
//                 {errors.roomId && <span className="aerm-err-msg">{errors.roomId}</span>}
//               </div>
              
//               <div className={`aerm-field ${errors.month ? 'aerm-field-err' : ''}`}>
//                 <label className="aerm-label">Month <span className="aerm-req">*</span></label>
//                 <select 
//                   className="aerm-select" 
//                   value={form.month} 
//                   onChange={e => set('month', e.target.value)}
//                   disabled={fetching}
//                 >
//                   {monthOptions.map(opt => (
//                     <option key={opt.value} value={opt.value}>{opt.label}</option>
//                   ))}
//                 </select>
//                 {errors.month && <span className="aerm-err-msg">{errors.month}</span>}
//               </div>
              
//               <div className={`aerm-field ${errors.year ? 'aerm-field-err' : ''}`}>
//                 <label className="aerm-label">Year <span className="aerm-req">*</span></label>
//                 <select 
//                   className="aerm-select" 
//                   value={form.year} 
//                   onChange={e => set('year', e.target.value)}
//                   disabled={fetching}
//                 >
//                   {getYearOptions().map(opt => (
//                     <option key={opt.value} value={opt.value}>{opt.label}</option>
//                   ))}
//                 </select>
//                 {errors.year && <span className="aerm-err-msg">{errors.year}</span>}
//               </div>

//               <div className={`aerm-field ${errors.previousReading ? 'aerm-field-err' : ''}`}>
//                 <label className="aerm-label">Previous Reading (Units) <span className="aerm-req">*</span></label>
//                 <input 
//                   className="aerm-input" 
//                   type="number" 
//                   step="0.01"
//                   placeholder="e.g. 1250.50" 
//                   value={form.previousReading} 
//                   onChange={e => set('previousReading', e.target.value)} 
//                 />
//                 {errors.previousReading && <span className="aerm-err-msg">{errors.previousReading}</span>}
//               </div>

//               <div className={`aerm-field ${errors.currentReading ? 'aerm-field-err' : ''}`}>
//                 <label className="aerm-label">Current Reading (Units) <span className="aerm-req">*</span></label>
//                 <input 
//                   className="aerm-input" 
//                   type="number" 
//                   step="0.01"
//                   placeholder="e.g. 1350.75" 
//                   value={form.currentReading} 
//                   onChange={e => set('currentReading', e.target.value)} 
//                 />
//                 {errors.currentReading && <span className="aerm-err-msg">{errors.currentReading}</span>}
//               </div>

//               <div className={`aerm-field ${errors.ratePerUnit ? 'aerm-field-err' : ''}`}>
//                 <label className="aerm-label">Rate per Unit (₹) <span className="aerm-req">*</span></label>
//                 <input 
//                   className="aerm-input" 
//                   type="number" 
//                   step="0.01"
//                   placeholder="e.g. 8.50" 
//                   value={form.ratePerUnit} 
//                   onChange={e => set('ratePerUnit', e.target.value)} 
//                 />
//                 {errors.ratePerUnit && <span className="aerm-err-msg">{errors.ratePerUnit}</span>}
//               </div>

//               {/* Summary Card */}
//               {units !== null && form.ratePerUnit && (
//                 <div className="aerm-summary">
//                   <div className="aerm-summary-item">
//                     <span className="aerm-summary-label">Units Consumed:</span>
//                     <span className="aerm-summary-value">{units.toFixed(2)} units</span>
//                   </div>
//                   <div className="aerm-summary-item">
//                     <span className="aerm-summary-label">Total Amount:</span>
//                     <span className="aerm-summary-value-highlight">₹ {(units * parseFloat(form.ratePerUnit)).toFixed(2)}</span>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Last 3 Readings Table - shown when room is selected */}
//           {form.roomId && (
//             <div className="aerm-last-readings-section">
//               <h4 className="aerm-section-title">Last 3 Readings</h4>
//               {loadingReadings ? (
//                 <div className="aerm-loading-readings">
//                   <div className="aerm-small-spinner"></div>
//                   <span>Loading reading history...</span>
//                 </div>
//               ) : lastThreeReadings.length > 0 ? (
//                 <div className="aerm-readings-table-wrapper">
//                   <table className="aerm-readings-table">
//                     <thead>
//                       <tr>
//                         <th>Month/Year</th>
//                         <th>Previous Reading</th>
//                         <th>Current Reading</th>
//                         <th>Units</th>
//                         <th>Rate/Unit</th>
//                         <th>Total Amount</th>
//                         <th>Reading Date</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {lastThreeReadings.map((reading, index) => (
//                         <tr key={reading.ReadingID || index}>
//                           <td>
//                             {monthOptions.find(m => m.value === reading.Month)?.label} {reading.Year}
//                           </td>
//                           <td>{reading.PreviousReading}</td>
//                           <td>{reading.CurrentReading}</td>
//                           <td>{reading.UnitsConsumed}</td>
//                           <td>₹{reading.RatePerUnit}</td>
//                           <td className="aerm-amount-cell">₹{reading.TotalAmount}</td>
//                           <td>{formatDate(reading.ReadingDate)}</td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               ) : (
//                 <div className="aerm-no-readings">
//                   <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
//                     <path d="M3 3v16a2 2 0 002 2h14a2 2 0 002-2V7l-5-5H5a2 2 0 00-2 2z"/>
//                     <path d="M13 3v4h4"/>
//                   </svg>
//                   <p>No previous readings found for this room.</p>
//                 </div>
//               )}
//             </div>
//           )}
//         </div>

//         {/* Footer */}
//         <div className="aerm-footer">
//           <button className="aerm-btn aerm-btn-outline" onClick={handleClose} disabled={loading}>
//             Cancel
//           </button>
//           <button className="aerm-btn aerm-btn-primary" onClick={handleSubmit} disabled={loading || fetching}>
//             {loading ? (
//               <>
//                 <span className="aerm-spinner"></span>
//                 {existingReading ? 'Updating...' : 'Adding...'}
//               </>
//             ) : (
//               <>
//                 <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
//                   <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
//                 </svg>
//                 {existingReading ? 'Update Reading' : 'Add Reading'}
//               </>
//             )}
//           </button>
//         </div>
//       </div>

//       <style>{`
//         .aerm-spinner, .aerm-small-spinner {
//           display: inline-block;
//           width: 12px;
//           height: 12px;
//           border: 2px solid #fff;
//           border-top-color: transparent;
//           border-radius: 50%;
//           animation: aerm-spin 0.6s linear infinite;
//           margin-right: 8px;
//         }
        
//         .aerm-small-spinner {
//           width: 14px;
//           height: 14px;
//           border: 2px solid #6366F1;
//           border-top-color: transparent;
//         }
        
//         @keyframes aerm-spin {
//           to { transform: rotate(360deg); }
//         }
        
//         .aerm-error-box {
//           margin: 16px 24px;
//           padding: 12px;
//           background: #FEF2F2;
//           border: 1px solid #FEE2E2;
//           border-radius: 8px;
//           color: #DC2626;
//           font-size: 13px;
//         }
        
//         .aerm-info-box {
//           margin: 16px 24px;
//           padding: 12px;
//           background: #EFF6FF;
//           border: 1px solid #BFDBFE;
//           border-radius: 8px;
//           color: #1D4ED8;
//           font-size: 13px;
//           display: flex;
//           align-items: center;
//           gap: 10px;
//         }
        
//         .aerm-loading-box {
//           margin: 16px 24px;
//           padding: 12px;
//           background: #F3F4F6;
//           border-radius: 8px;
//           color: #6B7280;
//           font-size: 13px;
//           display: flex;
//           align-items: center;
//           gap: 10px;
//         }
        
//         .aerm-summary {
//           grid-column: span 2;
//           background: #F0FDF4;
//           border: 1px solid #86EFAC;
//           border-radius: 12px;
//           padding: 16px;
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//         }
        
//         .aerm-summary-item {
//           display: flex;
//           flex-direction: column;
//           gap: 4px;
//         }
        
//         .aerm-summary-label {
//           font-size: 12px;
//           color: #6B7280;
//         }
        
//         .aerm-summary-value {
//           font-size: 18px;
//           font-weight: 600;
//           color: #166534;
//         }
        
//         .aerm-summary-value-highlight {
//           font-size: 20px;
//           font-weight: 700;
//           color: #16A34A;
//         }
        
//         /* Last 3 Readings Table Styles */
//         .aerm-last-readings-section {
//           margin-top: 24px;
//           padding-top: 16px;
//           border-top: 1px solid #E5E7EB;
//         }
        
//         .aerm-loading-readings {
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           gap: 12px;
//           padding: 32px;
//           background: #F9FAFB;
//           border-radius: 12px;
//           color: #6B7280;
//         }
        
//         .aerm-readings-table-wrapper {
//           overflow-x: auto;
//           border-radius: 12px;
//           border: 1px solid #E5E7EB;
//         }
        
//         .aerm-readings-table {
//           width: 100%;
//           border-collapse: collapse;
//           font-size: 13px;
//         }
        
//         .aerm-readings-table thead tr {
//           background: #F3F4F6;
//         }
        
//         .aerm-readings-table th {
//           padding: 12px 16px;
//           text-align: left;
//           font-weight: 600;
//           color: #374151;
//           border-bottom: 1px solid #E5E7EB;
//         }
        
//         .aerm-readings-table td {
//           padding: 12px 16px;
//           border-bottom: 1px solid #F3F4F6;
//           color: #4B5563;
//         }
        
//         .aerm-readings-table tr:last-child td {
//           border-bottom: none;
//         }
        
//         .aerm-readings-table tr:hover td {
//           background: #F9FAFB;
//         }
        
//         .aerm-amount-cell {
//           font-weight: 600;
//           color: #059669;
//         }
        
//         .aerm-no-readings {
//           display: flex;
//           flex-direction: column;
//           align-items: center;
//           justify-content: center;
//           gap: 12px;
//           padding: 40px;
//           background: #F9FAFB;
//           border-radius: 12px;
//           border: 1px dashed #D1D5DB;
//           color: #6B7280;
//         }
        
//         .aerm-no-readings p {
//           margin: 0;
//           font-size: 14px;
//         }
        
//         .aerm-room-info {
//           margin-top: 24px;
//           padding: 16px;
//           background: #F3F4F6;
//           border-radius: 12px;
//         }
        
//         .aerm-room-details {
//           display: grid;
//           grid-template-columns: repeat(2, 1fr);
//           gap: 12px;
//           margin-top: 12px;
//         }
        
//         .aerm-room-detail {
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//           padding: 8px 12px;
//           background: white;
//           border-radius: 8px;
//         }
        
//         .aerm-room-label {
//           font-size: 12px;
//           color: #6B7280;
//         }
        
//         .aerm-room-value {
//           font-size: 14px;
//           font-weight: 500;
//           color: #1F2937;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default AddElectricityReadingModal;
